import React, { useState } from "react";
import { Form, Formik } from "formik";
import styled, { keyframes } from "styled-components";
import * as Yup from "yup";
import TextInputField from "../../components/formik/TextInputField";
import { IconButton } from "../../components/Buttons";
import TextAreaField from "../../components/formik/TextareaField";
import { ArrowForward } from "../../shared/icons";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../store";
import { sendContactData } from "../../slices/contact";

// Styled Components
const Container = styled.div`
  width: 100%;
  background: #ffffff;
  border: 1px solid #eaeaea;
  border-radius: 10px;
  padding: 20px;
`;

const FieldWrapper = styled.div`
  margin-bottom: 24px;
`;

const ActionsWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MessageWrapper = styled.div`
  margin-top: 10px;
`;

// Create keyframes for the spinning animation
const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

// Styled component for the loader
const LoaderWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
`;

const Spinner = styled.div`
  width: 25px;
  height: 25px;
  border: 4px solid transparent;
  border-top: 4px solid #1e440b;
  border-radius: 50%;
  animation: ${spin} 1s linear infinite;
`;

const Loader: React.FC = () => {
  return (
    <LoaderWrapper>
      <Spinner />
    </LoaderWrapper>
  );
};

const SendMessageForm = () => {
  const dispatch: AppDispatch = useDispatch();
  const FormInitialValues = {
    firstName: "",
    email: "",
    phoneNumber: "",
    query: "",
  };

  const [initialValues, setInitialValues] = useState(FormInitialValues);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");

  const validationSchema = Yup.object({
    firstName: Yup.string().required("Firstname is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    phoneNumber: Yup.string()
      .matches(/^[0-9]+$/, "Phone number must contain only digits")
      .min(10, "Phone number must be at least 10 digits")
      .max(10, "Phone number cannot exceed 10 digits")
      .required("Phone number is required"),
    query: Yup.string().required("Message is required"),
  });

  const handleSubmit = (values: any, { resetForm }: any) => {
    const data = {
      name: values?.firstName,
      email: values?.email,
      phone_number: values?.phoneNumber,
      query: values?.query,
    };
    setIsLoading(true);
    dispatch(sendContactData(data))
      .then((res: any) => {
        console.log("res", res?.payload?.data?.data);
        if (res?.error) {
          throw new Error(res?.payload);
        }
        setMessage("Your query submitted successfully!");
        setTimeout(() => setMessage(""), 3000);
        resetForm();
      })
      .catch((err) => {
        setMessage("Something went wrong!");
        setTimeout(() => setMessage(""), 3000);
        console.log({err});
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <Container>
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        // enableReinitialize
      >
        {(props: any) => {
          return (
            <Form>
              <FieldWrapper>
                <TextInputField
                  label="First Name"
                  name="firstName"
                  type="text"
                  placeholder="Enter name"
                />
              </FieldWrapper>
              <FieldWrapper>
                <TextInputField
                  label="Email"
                  name="email"
                  type="text"
                  placeholder="Enter email"
                />
              </FieldWrapper>
              <FieldWrapper>
                <TextInputField
                  label="Phone Number"
                  name="phoneNumber"
                  type="text"
                  placeholder="Enter phone number"
                />
              </FieldWrapper>
              <FieldWrapper>
                <TextAreaField
                  label="Your Query"
                  name="query"
                  placeholder="Your message"
                />
              </FieldWrapper>
              <ActionsWrapper>
                <IconButton
                  variant="primary"
                  fullWidth
                  type="submit"
                  icon={<ArrowForward />}
                  disabled={isLoading}
                >
                  {isLoading ? <Loader /> : "Send"}
                </IconButton>
              </ActionsWrapper>
              <MessageWrapper>{message}</MessageWrapper>
            </Form>
          );
        }}
      </Formik>
    </Container>
  );
};

export default SendMessageForm;
